import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { ProyectoMock, EvaluacionItem } from '../mocks/proyectos';

const mapFromRow = (row: Record<string, unknown>): ProyectoMock => ({
  codigo: String(row.codigo ?? ''),
  titulo: String(row.titulo ?? ''),
  investigador: String(row.investigador ?? ''),
  email: String(row.email ?? ''),
  area: String(row.area ?? ''),
  categoria: String(row.categoria ?? 'Proyecto institucional'),
  estado: String(row.estado ?? 'Pendiente evaluación'),
  decision: String(row.decision ?? '—'),
  etica: String(row.etica ?? 'No'),
  porcentaje: Number(row.porcentaje ?? 0),
  fechaRecepcion: String(row.fecha_recepcion ?? ''),
  fechaDecision: String(row.fecha_decision ?? '—'),
  presupuesto: String(row.presupuesto ?? '—'),
  unidad: row.unidad ? String(row.unidad) : undefined,
  telefono: row.telefono ? String(row.telefono) : undefined,
  resumen: row.resumen ? String(row.resumen) : undefined,
  objetivos: row.objetivos ? String(row.objetivos) : undefined,
  diseno: row.diseno ? String(row.diseno) : undefined,
  cronograma: row.cronograma ? String(row.cronograma) : undefined,
  equipo: row.equipo ? String(row.equipo) : undefined,
  financiamiento: row.financiamiento ? String(row.financiamiento) : undefined,
  necesidad_etica: row.necesidad_etica ? String(row.necesidad_etica) : undefined,
  justificacion_etica: row.justificacion_etica ? String(row.justificacion_etica) : undefined,
  plan_publicacion: row.plan_publicacion ? String(row.plan_publicacion) : undefined,
  fecha_inicio: row.fecha_inicio ? String(row.fecha_inicio) : undefined,
  documentoNombre: row.documento_nombre ? String(row.documento_nombre) : undefined,
  documento_path: row.documento_path ? String(row.documento_path) : undefined,
  evaluaciones: undefined,
  scores_eval: undefined,
  observaciones_eval: undefined,
  ethics_eval: undefined,
});

const mapToRow = (p: ProyectoMock): Record<string, unknown> => ({
  codigo: p.codigo,
  titulo: p.titulo,
  investigador: p.investigador,
  email: p.email,
  area: p.area,
  categoria: p.categoria,
  estado: p.estado,
  decision: p.decision,
  etica: p.etica,
  porcentaje: p.porcentaje,
  fecha_recepcion: p.fechaRecepcion,
  fecha_decision: p.fechaDecision,
  presupuesto: p.presupuesto,
  unidad: p.unidad,
  telefono: p.telefono,
  resumen: p.resumen,
  objetivos: p.objetivos,
  diseno: p.diseno,
  cronograma: p.cronograma,
  equipo: p.equipo,
  financiamiento: p.financiamiento,
  necesidad_etica: p.necesidad_etica,
  justificacion_etica: p.justificacion_etica,
  plan_publicacion: p.plan_publicacion,
  fecha_inicio: p.fecha_inicio,
  documento_nombre: p.documentoNombre,
});

export function useSupabaseProyectos() {
  const [proyectos, setProyectos] = useState<ProyectoMock[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProyectos = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('ugpp_proyectos')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Error cargando proyectos:', error);
      setLoading(false);
      return;
    }
    const mapped = (data || []).map(mapFromRow);

    // Cargar evaluaciones
    const { data: evals } = await supabase.from('ugpp_evaluaciones').select('*');
    if (evals && evals.length > 0) {
      const evalByProject: Record<string, EvaluacionItem[]> = {};
      evals.forEach((e: Record<string, unknown>) => {
        const pid = String(e.proyecto_id ?? '');
        if (!evalByProject[pid]) evalByProject[pid] = [];
        evalByProject[pid].push({
          evaluador: String(e.evaluador ?? ''),
          fecha: String(e.fecha ?? ''),
          scores: (e.scores as Record<string, string>) ?? {},
          observaciones: String(e.observaciones ?? ''),
          ethicsFlag: String(e.ethics_flag ?? ''),
          recomendacion: String(e.recomendacion ?? ''),
          decisionPropuesta: (e.decision_propuesta as 'aprobado' | 'correcciones' | 'no_pertinente' | '') ?? '',
        });
      });
      mapped.forEach(p => {
        // Buscar por título/ email aproximado ya que no tenemos el uuid aquí
        // El approach correcto es buscar por código
        const key = String(p.codigo || '');
        const evs = evalByProject[key];
        if (evs) (p as { evaluaciones: EvaluacionItem[] }).evaluaciones = evs;
      });
    }

    setProyectos(mapped);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProyectos();
  }, [fetchProyectos]);

  const addProyecto = useCallback(async (p: ProyectoMock): Promise<boolean> => {
    const { data: existing } = await supabase
      .from('ugpp_proyectos')
      .select('id')
      .eq('codigo', p.codigo)
      .maybeSingle();
    if (existing) return false;

    const { error } = await supabase.from('ugpp_proyectos').insert(mapToRow(p));
    if (error) {
      console.error('Error insertando proyecto:', error);
      return false;
    }
    await fetchProyectos();
    return true;
  }, [fetchProyectos]);

  const updateProyecto = useCallback(async (codigo: string, updates: Partial<ProyectoMock>): Promise<boolean> => {
    const row: Record<string, unknown> = {};
    if (updates.estado !== undefined) row.estado = updates.estado;
    if (updates.decision !== undefined) row.decision = updates.decision;
    if (updates.porcentaje !== undefined) row.porcentaje = updates.porcentaje;
    if (updates.fechaDecision !== undefined) row.fecha_decision = updates.fechaDecision;
    if (updates.categoria !== undefined) row.categoria = updates.categoria;
    if (updates.documentoNombre !== undefined) row.documento_nombre = updates.documentoNombre;
    if (updates.documento_path !== undefined) row.documento_path = updates.documento_path;

    if (Object.keys(row).length === 0) return true;

    const { error } = await supabase
      .from('ugpp_proyectos')
      .update(row)
      .eq('codigo', codigo);

    if (error) {
      console.error('Error actualizando proyecto:', error);
      return false;
    }
    await fetchProyectos();
    return true;
  }, [fetchProyectos]);

  return { proyectos, loading, addProyecto, updateProyecto, refresh: fetchProyectos };
}